'use client';

import { useState, useEffect, useRef, Suspense } from 'react';
import { useRouter, useSearchParams } from 'next/navigation';
import { useSignIn, useUser, useClerk, useAuth } from '@clerk/nextjs';
import posthog from 'posthog-js';
import Link from 'next/link';
import Image from 'next/image';
import Button from '@/app/components/ui/Button/Button';
import CodeInputGrid from '@/app/components/CodeInputGrid';
import PasswordInput from '@/app/components/PasswordInput';
import { clearUserData } from '@/utils/clearUserData';
import { GoogleIcon, OrDivider } from './oauth-ui';

// Best-effort JWT payload decoder — same shape used in /sso-callback. We
// only inspect a public claim (`external_id`); signature verification is
// done server-side by the middleware.
function decodeJwtPayload(token) {
  try {
    const payloadStr = token?.split('.')[1];
    if (!payloadStr) return null;
    const padded = payloadStr.replace(/-/g, '+').replace(/_/g, '/');
    return JSON.parse(
      atob(padded + '='.repeat((4 - (padded.length % 4)) % 4))
    );
  } catch {
    return null;
  }
}

function LoginContent() {
  const { signIn, setActive, isLoaded } = useSignIn();
  const { isLoaded: userLoaded, isSignedIn, user } = useUser();
  const { signOut } = useClerk();
  const { getToken } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [info, setInfo] = useState('');
  const [loading, setLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [signOutLoading, setSignOutLoading] = useState(false);
  const [recovering, setRecovering] = useState(false);
  // Clerk's Client Trust ("device_trust") feature surfaces as
  // needs_second_factor in the v6 SDK, even when no MFA strategy is enrolled.
  // These pieces of state drive the email-code step when that happens. The
  // code is held as a 6-cell array to mirror /reset-password-with-code's
  // input UX (one digit per box, auto-advance, paste support).
  const [awaitingSecondFactor, setAwaitingSecondFactor] = useState(false);
  const [secondFactorEmail, setSecondFactorEmail] = useState('');
  const [secondFactorEmailId, setSecondFactorEmailId] = useState('');
  const [code, setCode] = useState(['', '', '', '', '', '']);
  const recoveryAttempted = useRef(false);
  const router = useRouter();
  const searchParams = useSearchParams();
  const redirectTo = searchParams.get('redirectTo') || '/library';
  const message = searchParams.get('message');

  /**
   * Kick off Clerk's OAuth Google flow. Returns to /sso-callback with
   * `?state=...`; that page completes the Clerk handshake and forwards the
   * user on.
   *
   * The OAuth round-trip loses our `redirectTo` query param (Clerk owns the
   * redirectUrl, and /sso-callback hard-navigates afterwards). To support
   * flows that must come back here — notably the Electron desktop handoff at
   * /login/desktop-auth?code_challenge=... — we stash `redirectTo` in
   * sessionStorage (per-tab, survives the Google bounce) and /sso-callback
   * reads it back instead of defaulting to /library.
   */
  const handleGoogleSignIn = async () => {
    if (!isLoaded || googleLoading) return;
    setError('');
    setGoogleLoading(true);
    try {
      clearUserData();
      // Preserve where to land after OAuth across the Google round-trip.
      // (clearUserData only clears specific keys, so set this after it.)
      try {
        if (redirectTo && redirectTo !== '/library') {
          sessionStorage.setItem('flare_oauth_redirect', redirectTo);
        } else {
          sessionStorage.removeItem('flare_oauth_redirect');
        }
      } catch (_) {
        // sessionStorage unavailable — Google still works, lands on /library
      }
      await signIn.authenticateWithRedirect({
        strategy: 'oauth_google',
        redirectUrl: '/sso-callback',
        redirectUrlComplete: '/library',
      });
      // authenticateWithRedirect navigates the browser away; we never reach
      // the code below unless something failed before redirect.
    } catch (err) {
      console.error('[login] Google sign-in failed to start:', err);
      const clerkError = err?.errors?.[0];
      setError(
        clerkError?.message ||
          'Could not start Google sign-in. Please try again.'
      );
      setGoogleLoading(false);
    }
  };

  // Clear any cached user data when login page mounts (but only when not
  // already signed in — clearing data for an authenticated user would log
  // them out from their own perspective).
  useEffect(() => {
    if (!isSignedIn) {
      clearUserData();
    }
  }, [isSignedIn]);

  /**
   * Stuck-state recovery for OAuth users whose Clerk session has no
   * external_id claim — the symptom is being bounced from any protected
   * page to /login while signed in. Causes (any of):
   *  - Clerk webhook never reached the server (no public tunnel in dev,
   *    misconfigured webhook URL in prod) so externalId was never patched.
   *  - The old webhook-only path failed on the unique-email constraint
   *    (existing Prisma row, never linked).
   *  - Clerk session token customization missing in the dashboard for
   *    this instance.
   *
   * We POST to /api/auth/ensure-prisma-link to either link the Clerk user
   * to an existing Prisma row (by email) or mint a new one, then refresh
   * the session token to pick up the claim. If the dashboard customization
   * is missing the claim never appears even after linking — we surface a
   * descriptive error after a few attempts so the user knows it's a
   * config issue rather than an infinite spinner.
   */
  useEffect(() => {
    if (!userLoaded || !isSignedIn) return;
    if (recoveryAttempted.current) return;
    recoveryAttempted.current = true;

    let cancelled = false;
    (async () => {
      try {
        const token = await getToken({ skipCache: true });
        if (cancelled) return;
        const decoded = decodeJwtPayload(token);
        if (decoded?.external_id) {
          // Healthy session — let the existing "already signed in" UI render.
          return;
        }

        console.info(
          '[login] Clerk session is signed in but lacks external_id; running ensure-prisma-link recovery'
        );
        setRecovering(true);

        const linkRes = await fetch('/api/auth/ensure-prisma-link', {
          method: 'POST',
          credentials: 'include',
        });
        const linkBody = await linkRes.json().catch(() => ({}));
        if (!linkRes.ok) {
          console.error(
            '[login] ensure-prisma-link failed:',
            linkRes.status,
            linkBody
          );
          setRecovering(false);
          setError(
            linkBody?.error ||
              'We could not finish linking your account. Please sign out and try again.'
          );
          return;
        }
        console.info('[login] ensure-prisma-link ok:', linkBody);

        // Poll for the refreshed claim — Clerk's token generation can lag
        // the user-record update by a few hundred ms.
        for (let attempt = 0; attempt < 5; attempt += 1) {
          if (cancelled) return;
          // eslint-disable-next-line no-await-in-loop
          const refreshed = await getToken({ skipCache: true });
          const refreshedPayload = refreshed ? decodeJwtPayload(refreshed) : null;
          if (refreshedPayload?.external_id) {
            window.location.assign(redirectTo);
            return;
          }
          // eslint-disable-next-line no-await-in-loop
          await new Promise((resolve) => setTimeout(resolve, 200 * (attempt + 1)));
        }

        // Linking succeeded but the claim never appeared. Almost always
        // means Clerk dashboard → Sessions → Customize session token is
        // missing the external_id mapping for this instance.
        console.error(
          '[login] Linking succeeded but session token still missing external_id — check Clerk dashboard session customization'
        );
        setRecovering(false);
        setError(
          'Account linked, but your session is missing a required claim. An admin needs to add {"external_id": "{{user.external_id}}"} to Clerk Sessions → Customize session token.'
        );
      } catch (err) {
        if (cancelled) return;
        console.error('[login] Stuck-state recovery threw:', err);
        setRecovering(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [userLoaded, isSignedIn, getToken, redirectTo]);

  const handleSignOutForSwitch = async () => {
    setSignOutLoading(true);
    try {
      clearUserData();
      await signOut();
      // useUser will flip to signed-out on the next render; form will appear.
    } catch (err) {
      console.error('[login] Sign-out failed:', err);
      setError('Sign-out failed. Please try again.');
    } finally {
      setSignOutLoading(false);
    }
  };

  // Shared "you're in, take it from here" path. Used by both the password
  // sign-in success branch and the second-factor (Client Trust / MFA) success
  // branch. Keeps the posthog identify + subscription-status redirect logic
  // in one place instead of duplicating it across two code paths.
  const completeSignIn = async (result) => {
    await setActive({ session: result.createdSessionId });

    try {
      const externalId = result?.userData?.externalId || signIn?.userData?.externalId || null;
      if (externalId && typeof posthog.identify === 'function') {
        posthog.identify(externalId, email ? { email } : undefined);
      }
    } catch (_) {
      // swallow — analytics must never block login
    }

    let destination = redirectTo;
    if (redirectTo === '/library') {
      try {
        const subResp = await fetch('/api/subscription/status');
        if (subResp.ok) {
          const subData = await subResp.json();
          if (!subData?.isActive) destination = '/subscribe';
        }
      } catch (_) {
        // fall through to default
      }
    }

    window.location.assign(destination);
  };

  // Reset the 6-cell code input back to empty. CodeInputGrid refocuses
  // the first cell when it sees the array transition to all-empty.
  const resetCodeInputs = () => {
    setCode(['', '', '', '', '', '']);
  };

  // Submit the typed code to Clerk. Called by CodeInputGrid's onComplete
  // once all 6 cells are filled (typing the last digit or pasting). No
  // separate submit button — matches the same auto-advance behavior used
  // by /reset-password-with-code and /confirm-email.
  const handleSecondFactor = async (codeString) => {
    if (!codeString || codeString.length !== 6) return;
    setError('');
    setInfo('');
    setLoading(true);
    try {
      const result = await signIn.attemptSecondFactor({
        strategy: 'email_code',
        code: codeString,
      });
      if (result.status === 'complete') {
        await completeSignIn(result);
        return;
      }
      console.warn('[login] attemptSecondFactor returned non-complete:', result.status);
      setError('Verification did not complete. Please try again.');
      resetCodeInputs();
    } catch (err) {
      console.error('[login][debug] attemptSecondFactor error:', {
        code: err?.errors?.[0]?.code,
        message: err?.errors?.[0]?.message,
        allErrors: err?.errors,
        fullErr: err,
      });
      const clerkError = err?.errors?.[0];
      const errCode = clerkError?.code;
      if (
        errCode === 'form_code_incorrect' ||
        errCode === 'verification_failed' ||
        errCode === 'verification_expired'
      ) {
        setError('Invalid or expired code. Please request a new one.');
      } else {
        setError(clerkError?.message || 'Could not verify the code. Please try again.');
      }
      resetCodeInputs();
    } finally {
      setLoading(false);
    }
  };

  const handleResendCode = async () => {
    setError('');
    setInfo('');
    try {
      await signIn.prepareSecondFactor({
        strategy: 'email_code',
        emailAddressId: secondFactorEmailId || undefined,
      });
      setInfo('A new code has been sent.');
      resetCodeInputs();
    } catch (err) {
      console.error('[login] resend code failed:', err);
      setError(err?.errors?.[0]?.message || 'Could not resend code. Please try again.');
    }
  };

  // "Use a different account" / back link. Drops the in-progress signIn
  // resource by resetting the form state — Clerk's signIn is held in
  // useSignIn()'s hook state and a new signIn.create() will replace it.
  const handleResetToPassword = () => {
    setAwaitingSecondFactor(false);
    setCode(['', '', '', '', '', '']);
    setSecondFactorEmail('');
    setSecondFactorEmailId('');
    setError('');
    setInfo('');
  };

  /**
   * Try to log in via Clerk. If the user isn't in Clerk yet, check whether
   * they exist in our Prisma DB (a pre-Clerk-migration user) and, if so,
   * provision their Clerk account + send a password-setup email.
   */
  const handleLogin = async (event) => {
    event.preventDefault();
    setError('');
    setInfo('');
    if (!isLoaded) return;
    setLoading(true);

    try {
      const result = await signIn.create({
        identifier: email,
        password,
        strategy: 'password',
      });

      if (result.status === 'complete') {
        await completeSignIn(result);
        return;
      }

      // Clerk gates this sign-in on a second factor. In our instance that's
      // Client Trust ("device_trust") sending an email code on first sign-in
      // from a new browser/device; the same code path also covers MFA if it
      // ever gets turned on. Find the email-code factor, ask Clerk to send
      // the code, and flip the form into code-entry mode.
      if (result.status === 'needs_second_factor') {
        const emailFactor = (result.supportedSecondFactors || []).find(
          (f) => f.strategy === 'email_code'
        );
        if (!emailFactor) {
          console.warn(
            '[login] needs_second_factor without email_code factor — supported:',
            result.supportedSecondFactors
          );
          setError('Unable to sign in. Please contact support if this persists.');
          return;
        }
        try {
          await signIn.prepareSecondFactor({
            strategy: 'email_code',
            emailAddressId: emailFactor.emailAddressId,
          });
          setSecondFactorEmail(emailFactor.safeIdentifier || email);
          setSecondFactorEmailId(emailFactor.emailAddressId || '');
          setAwaitingSecondFactor(true);
        } catch (prepErr) {
          console.error('[login] prepareSecondFactor failed:', prepErr);
          setError(
            prepErr?.errors?.[0]?.message ||
              'Could not send verification code. Please try again.'
          );
        }
        return;
      }

      console.warn('[login] Unexpected signIn status:', result.status);
      setError('Unable to sign in. Please contact support if this persists.');
    } catch (err) {
      const clerkError = err?.errors?.[0];
      const code = clerkError?.code;

      // Diagnostic: log the full error so we can see the actual code Clerk
      // returned (form_identifier_not_found, form_password_incorrect, etc.)
      console.log('[login][debug] signIn.create error:', {
        code,
        message: clerkError?.message,
        allErrors: err?.errors,
        fullErr: err,
      });

      // Lazy migration trigger: user isn't in Clerk yet. Check Prisma and,
      // if they exist there, provision the Clerk account and send a reset email.
      if (code === 'form_identifier_not_found') {
        try {
          const checkResp = await fetch('/api/auth/migration-check', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ email }),
          });
          const checkData = await checkResp.json();
          console.log('[login][debug] migration-check response:', {
            status: checkResp.status,
            body: checkData,
          });

          if (checkData?.migrationNeeded) {
            // Account was just provisioned in Clerk with no password.
            // Trigger Clerk's password-reset email so the user can set one,
            // then navigate them directly to the page where they enter the
            // code + new password. Otherwise the "check your email" banner
            // is a dead-end — users have nowhere to put the code.
            try {
              await signIn.create({
                strategy: 'reset_password_email_code',
                identifier: email,
              });
              console.log('[login][debug] reset email triggered for', email);
            } catch (resetErr) {
              console.error('[login] Failed to send reset email post-migration:', resetErr);
            }
            const params = new URLSearchParams({
              email,
              migration: '1',
              message: checkData.message || '',
            });
            router.push(`/reset-password-with-code?${params.toString()}`);
            return;
          }

          console.log('[login][debug] migration-check returned migrationNeeded=false → falling through to invalid-credentials message');
        } catch (checkErr) {
          console.error('[login] migration-check failed:', checkErr);
        }

        // Genuinely unknown account
        setError('Invalid email or password.');
        return;
      }

      if (code === 'form_password_incorrect' || code === 'form_password_invalid') {
        // Stuck-migration recovery: a user who triggered the migration flow
        // but never completed the password reset (consumed the email code
        // but didn't submit a new password) ends up with a Clerk account
        // they can't log into — they don't know the temp password we set.
        // migration-check returns migrationNeeded:true for these users
        // (via publicMetadata.pendingPasswordSetup); we route them through
        // the reset flow with a fresh email. For users who actually just
        // typed the wrong password, migration-check returns false and we
        // show the normal error.
        try {
          const checkResp = await fetch('/api/auth/migration-check', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ email }),
          });
          const checkData = await checkResp.json();
          if (checkData?.migrationNeeded) {
            try {
              await signIn.create({
                strategy: 'reset_password_email_code',
                identifier: email,
              });
            } catch (resetErr) {
              console.error('[login] Failed to send reset email for stuck migration:', resetErr);
            }
            const params = new URLSearchParams({
              email,
              migration: '1',
              message: checkData.message || "Check your email for a code to finish setting up your account.",
            });
            router.push(`/reset-password-with-code?${params.toString()}`);
            return;
          }
        } catch (checkErr) {
          console.error('[login] migration-check on wrong-password failed:', checkErr);
        }

        setError('Invalid email or password.');
        return;
      }

      // User exists in Clerk but has no password set — happens to migrated
      // users whose password-reset flow didn't persist (legacy bug) or to
      // anyone whose account was provisioned passwordless. Route them
      // through the regular forgot-password flow so they can set one.
      if (code === 'strategy_for_user_invalid') {
        try {
          await signIn.create({
            strategy: 'reset_password_email_code',
            identifier: email,
          });
        } catch (resetErr) {
          console.error('[login] Failed to send reset email for passwordless user:', resetErr);
        }
        const params = new URLSearchParams({
          email,
          migration: '1',
          message:
            "Your account needs a password set up. Check your email for a code, then create a new password.",
        });
        router.push(`/reset-password-with-code?${params.toString()}`);
        return;
      }

      if (code === 'form_identifier_not_verified' || code === 'verification_required') {
        router.push(`/confirm-email?email=${encodeURIComponent(email)}&resend=true`);
        return;
      }

      console.error('[login] Sign-in failed:', err);
      setError(clerkError?.message || 'Sign in failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Recovery in progress for a signed-in user whose session lacks
  // external_id — render a spinner so they don't see a confusing
  // "already signed in" UI mid-link.
  if (recovering) {
    return (
      <div className="auth-page">
        <div className="auth-container">
          <div className="auth-right-container-wrapper">
            <div className="auth-right-container">
              <div className="auth-right-content" style={{ textAlign: 'center', gap: 16 }}>
                <div className="loading-spinner" />
                <p style={{ color: 'var(--text-secondary, #9CA3AF)', fontSize: 13 }}>
                  Finishing setting up your account…
                </p>
              </div>
            </div>
          </div>
          <div className="auth-left-container">
            <Image src="/logoFlare.png" alt="Flare Logo" width={160} height={84} priority />
          </div>
        </div>
      </div>
    );
  }

  // If the user is already signed in, show a friendly "you're already
  // logged in" UI instead of the form. Avoids the confusing
  // "You're already signed in." error from Clerk when they submit the
  // form, and gives them a clear path to either continue or switch
  // accounts. Wait for userLoaded so we don't flash the form first.
  if (userLoaded && isSignedIn && user) {
    const displayEmail =
      user.primaryEmailAddress?.emailAddress ||
      user.emailAddresses?.[0]?.emailAddress ||
      'your account';
    const displayName = user.firstName || user.fullName || null;

    return (
      <div className="auth-page">
        <div className="auth-container">
          <div className="auth-right-container-wrapper">
            <div className="auth-right-container">
              <div className="auth-right-content">
                <div className="auth-right-header">
                  <h1>Already signed in</h1>
                  <p>
                    You&apos;re logged in as{' '}
                    <strong style={{ color: 'var(--text-primary, #FFFFFF)' }}>
                      {displayEmail}
                    </strong>
                    {displayName ? ` (${displayName})` : ''}.
                  </p>
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', width: '100%' }}>
                  <Button
                    variant="primary"
                    size="md"
                    fullWidth
                    disabled={signOutLoading}
                    onClick={() => window.location.assign(redirectTo)}
                  >
                    Continue to {redirectTo === '/library' ? 'library' : redirectTo}
                  </Button>

                  <Button
                    variant="secondary"
                    size="md"
                    fullWidth
                    loading={signOutLoading}
                    onClick={handleSignOutForSwitch}
                  >
                    Sign out and use a different account
                  </Button>
                </div>

                {error && <div className="error-message">{error}</div>}
              </div>
            </div>
          </div>
          <div className="auth-left-container">
            <Image
              src="/logoFlare.png"
              alt="Flare Logo"
              width={160}
              height={84}
              priority
            />
          </div>
        </div>
      </div>
    );
  }

  // Second-factor (Client Trust / MFA) step. Reached when Clerk returns
  // status='needs_second_factor' after a correct password. The signIn
  // resource is held in useSignIn()'s hook state, so we must stay on this
  // route — navigating away would drop it and force the user to re-enter
  // their password.
  if (awaitingSecondFactor) {
    return (
      <div className="auth-page">
        <div className="auth-container">
          <div className="auth-right-container-wrapper">
            <div className="auth-right-container">
              <div className="auth-right-content">
                <div className="auth-right-header">
                  <h1>Verify it&apos;s you</h1>
                  <p>
                    We sent a verification code to{' '}
                    <strong style={{ color: 'var(--text-primary, #FFFFFF)' }}>
                      {secondFactorEmail}
                    </strong>
                    . Enter it below to finish signing in.
                  </p>
                </div>

                {info && (
                  <div className="bg-emerald-500/10 border border-emerald-500/20 rounded-lg p-4 w-full">
                    <p className="text-emerald-300 text-sm">{info}</p>
                  </div>
                )}

                {error && <div className="error-message">{error}</div>}

                <CodeInputGrid
                  value={code}
                  onChange={setCode}
                  onComplete={handleSecondFactor}
                  loading={loading}
                />

                <p className="auth-link">
                  Didn&apos;t get a code?{' '}
                  <button
                    type="button"
                    onClick={handleResendCode}
                    disabled={loading}
                    style={{ background: 'none', border: 'none', color: 'var(--accent, #3b82f6)', cursor: 'pointer', padding: 0, font: 'inherit', textDecoration: 'underline' }}
                  >
                    Resend it
                  </button>
                  {' '}or{' '}
                  <button
                    type="button"
                    onClick={handleResetToPassword}
                    disabled={loading}
                    style={{ background: 'none', border: 'none', color: 'var(--accent, #3b82f6)', cursor: 'pointer', padding: 0, font: 'inherit', textDecoration: 'underline' }}
                  >
                    use a different account
                  </button>
                  .
                </p>
              </div>
            </div>
          </div>
          <div className="auth-left-container">
            <Image
              src="/logoFlare.png"
              alt="Flare Logo"
              width={160}
              height={84}
              priority
            />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="auth-page">
      <div className="auth-container">
        {/* right side */}
        {/* Contains actual form for login */}
        <div className="auth-right-container-wrapper">
          <div className="auth-right-container">
            <div className="auth-right-content">
              <div className="auth-right-header">
                <Link href="/" className="inline-block mb-2">
                  {/* <div className="flex items-center justify-center">
                    <Image
                      src="/logoFlare.png"
                      alt="Flare Logo"
                      width={80}
                      height={42}
                      priority
                    />
                  </div> */}
                </Link>
                <h1>Welcome Back</h1>
                <p>
                  Join Flare, the fastest and easiest way to make
                  professional-quality videos. Needs copy.
                </p>
              </div>

              {message && (
                <div className="bg-blue-500/10 border border-blue-500/20 rounded-lg p-4 w-full">
                  <div className="flex gap-3 items-center text-blue-400">
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <p>{message}</p>
                  </div>
                </div>
              )}

              {info && (
                <div className="bg-emerald-500/10 border border-emerald-500/20 rounded-lg p-4 w-full">
                  <p className="text-emerald-300 text-sm">{info}</p>
                </div>
              )}

              {error && <div className="error-message">{error}</div>}

              <Button
                variant="secondary"
                size="md"
                fullWidth
                loading={googleLoading}
                disabled={!isLoaded}
                onClick={handleGoogleSignIn}
                iconLeft={<GoogleIcon />}
              >
                Continue with Google
              </Button>

              <OrDivider />

              <form onSubmit={handleLogin}>
                <label>
                  Email
                  <input
                    id="email"
                    name="email"
                    type="email"
                    autoComplete="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="example@mail.com"
                  />
                </label>

                <label>
                  Password
                  <PasswordInput
                    id="password"
                    name="password"
                    autoComplete="current-password"
                    required
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="••••••••"
                  />
                </label>

                <Link href="/forgot-password" id="forgot-password">
                  Forgot Password?
                </Link>

                <button
                  type="submit"
                  disabled={loading || !isLoaded}
                >
                  {loading ? "Logging in..." : "Log in"}
                </button>
              </form>

              <p className="auth-link">
                Don&apos;t have an account?{' '}
                <Link href="/register" id="signup-link">
                  Sign up
                </Link>
              </p>
            </div>
          </div>
        </div>
        {/* left side */}
        {/* Contains image */}
        <div className="auth-left-container">
          <Image
            src="/logoFlare.png"
            alt="Flare Logo"
            width={160}
            height={84}
            priority
          />
        </div>
      </div>
    </div>
  );
}

export default function LoginForm() {
  return (
    <Suspense fallback={
      <div className="min-h-screen flex items-center justify-center">
        <div className="loading-spinner" />
      </div>
    }>
      <LoginContent />
    </Suspense>
  );
}
